import React, { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import axios from "axios";

import TopBannerSection from "../../components/Homepage/A.TopBannerSection";
import HeroSection from "../../components/Homepage/B.Herosection";
import CategoriesSection from "../../components/Homepage/C.Categorysection";
import DemoVideoSection from "../../components/Homepage/D.Demovideosection";
import CoursesSection from "../../components/Homepage/E.Coursesection";
import WhyChooseUsSection from "../../components/Homepage/F.WhyChooseUsSection";
import CtaSection from "../../components/Homepage/G.CtaSection";
import WhatWeOfferSection from "../../components/Homepage/H.Whatweoffer";
import FeedbackSection from "../../components/Homepage/I.Feedbacksection";
import AnnouncementPopup from "../../components/page/AnnouncementPopup";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1001;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PageWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const SkeletonLoader = styled.div`
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HomePage = () => {
  const context = useOutletContext();
  const onSignUpClick = context ? context.onSignUpClick : null;

  const [announcement, setAnnouncement] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  // ✅ NEW: control staggered rendering
  const [visibleSections, setVisibleSections] = useState(0);

  const contextValue = useMemo(() => ({ onSignUpClick }), [onSignUpClick]);

  // ✅ EXISTING: Announcement fetch (unchanged)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAnnouncement();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL || "http://localhost:8000"}/api/announcements/latest`,
        { signal: controller.signal },
      );

      clearTimeout(timeoutId);

      if (response.data) {
        setAnnouncement(response.data);
        setShowPopup(true);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.log("Announcement fetch skipped/error");
      }
    }
  };

  // ✅ NEW: stagger logic (sleep effect)
  useEffect(() => {
    let index = 0;

    const interval = setInterval(() => {
      index++;
      setVisibleSections(index);

      if (index >= 8) {
        clearInterval(interval);
      }
    }, 300); // adjust delay here

    return () => clearInterval(interval);
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <PageWrapper>
      {/* Overlay */}
      {showPopup && <PageOverlay onClick={handleClosePopup} />}

      {/* Popup */}
      {showPopup && (
        <AnnouncementPopup
          announcement={announcement}
          onClose={handleClosePopup}
        />
      )}

      {/* Always render first section */}
      <TopBannerSection onSignUpClick={onSignUpClick} />

      {/* Staggered sections */}
      {visibleSections >= 1 ? <HeroSection /> : <SkeletonLoader />}
      {visibleSections >= 2 ? <CategoriesSection /> : <SkeletonLoader />}
      {visibleSections >= 3 ? <DemoVideoSection /> : <SkeletonLoader />}
      {visibleSections >= 4 ? <CoursesSection /> : <SkeletonLoader />}
      {visibleSections >= 5 ? <WhyChooseUsSection /> : <SkeletonLoader />}
      {visibleSections >= 6 ? <CtaSection /> : <SkeletonLoader />}
      {visibleSections >= 7 ? <WhatWeOfferSection /> : <SkeletonLoader />}
      {visibleSections >= 8 ? <FeedbackSection /> : <SkeletonLoader />}
    </PageWrapper>
  );
};

export default React.memo(HomePage);
